import { useCallback } from "react";

import type { InputMaybe, StockTypeTarget, StockTypeTerm } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";

type Props = {
	stockTypeTerm: ForNodeType<InputMaybe<StockTypeTerm> | undefined> | undefined;
	stockTypeTarget: ForNodeType<InputMaybe<StockTypeTarget> | undefined> | undefined;
	maxDailyStockNum: ForNodeType<InputMaybe<number> | undefined> | undefined;

	onSetViewModel: (
		values: {
			key: "stockTypeTerm" | "stockTypeTarget" | "maxDailyStockNum";
			value:
				| ForNodeType<InputMaybe<StockTypeTerm> | undefined>
				| ForNodeType<InputMaybe<StockTypeTarget> | undefined>
				| ForNodeType<InputMaybe<number> | undefined>
				| undefined;
		}[],
	) => void;
};

export const usePlanStockTarget = ({ stockTypeTarget, stockTypeTerm, maxDailyStockNum, onSetViewModel }: Props) => {
	const onSetStockTypeTerm = useCallback(
		(input: StockTypeTerm) => {
			onSetViewModel([
				{
					key: "stockTypeTerm",
					value: { isEdited: false, value: input },
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetStockTypeTarget = useCallback(
		(input: StockTypeTarget) => {
			onSetViewModel([
				{
					key: "stockTypeTarget",
					value: { isEdited: false, value: input },
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetMaxDailyStockNum = useCallback(
		(input: number | undefined) => {
			onSetViewModel([
				{
					key: "maxDailyStockNum",
					value: {
						isEdited: false,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const isEdited = stockTypeTerm?.isEdited ?? stockTypeTarget?.isEdited ?? maxDailyStockNum?.isEdited ?? false;
	const hasError = Boolean(stockTypeTerm?.error ?? stockTypeTarget?.error ?? maxDailyStockNum?.error ?? false);

	return {
		isEdited,
		hasError,
		stockTypeTerm,
		onSetStockTypeTerm,
		stockTypeTarget,
		onSetStockTypeTarget,
		maxDailyStockNum,
		onSetMaxDailyStockNum,
	};
};
